import { useNavigate } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { useEffect, useMemo, useState } from "react";
import { Snackbar } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import Societe from "../../services/Societe";
import getCurrentUserId from "../../reduxSaga/sagas/requests/currentUserId";
import SellingService from "../../services/SellingService";
import FactureNonConfirme from "./Table/FactureNonConfirme";
import withLoader from "../../components/layouts/WithLoader";

const animatedComponents = makeAnimated();
const ALL_CLIENTS = { value: 0, label: "Tous" };
const CREATE_ROLES = ["ADMIN", "PURCHASING_MANAGER"];
const fmt = (v) => parseFloat(Number(v).toFixed(3));

const cardStyle = { borderRadius: 8, boxShadow: "0 1px 6px rgba(0,0,0,.08)" };
const labelStyle = {
  fontSize: "0.8rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  color: "#6c757d",
};

const KpiCard = ({ label, value, icon, color, children }) => (
  <div className="col-lg col-md-4 col-sm-6">
    <div
      className="d-flex align-items-center p-3 mb-3 bg-white"
      style={{ ...cardStyle, borderLeft: `4px solid ${color}` }}
    >
      <span
        className="d-flex align-items-center justify-content-center text-white mr-3"
        style={{ width: 44, height: 44, borderRadius: 8, backgroundColor: color }}
      >
        <i className={icon}></i>
      </span>
      <div>
        <small
          className="text-muted text-uppercase d-block"
          style={{ fontWeight: 600, letterSpacing: "0.03em" }}
        >
          {label}
        </small>
        <span style={{ fontSize: "1.3rem", fontWeight: 700 }}>{value}</span>
        {children}
      </div>
    </div>
  </div>
);

const AddButton = ({ onClick, children, variant = "dark" }) => (
  <button
    className={`btn btn-${variant} mt-2 mt-sm-0 ml-sm-2 d-inline-flex align-items-center`}
    style={{ borderRadius: 8, padding: "8px 18px 8px 10px", fontWeight: 500 }}
    onClick={onClick}
  >
    <span
      className="d-inline-flex align-items-center justify-content-center mr-2"
      style={{
        width: 26,
        height: 26,
        borderRadius: 6,
        backgroundColor: variant.startsWith("outline") ? "rgba(0,0,0,.08)" : "rgba(255,255,255,.18)",
      }}
    >
      <i className="fas fa-plus" style={{ fontSize: 12 }} />
    </span>
    {children}
  </button>
);

const AllFactureSell = ({ startLoading, stopLoading }) => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(ALL_CLIENTS);
  const [dateBeginRaw, setDateBeginRaw] = useState("");
  const [dateEndRaw, setDateEndRaw] = useState("");
  const [inConfirmedFacture, setInConfirmedFacture] = useState([]);
  const [chargedFactureInfo, setChargedFactureInfo] = useState(false);
  const [alert, setAlert] = useState({ open: false, text: "" });

  const showError = (text) => setAlert({ open: true, text });
  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") return;
    setAlert((a) => ({ ...a, open: false }));
  };

  const canCreate = CREATE_ROLES.some((r) => userRole.includes(r));

  const loadLastMonth = () => {
    startLoading();
    SellingService.getFactureOfLastMonthOptimized(getCurrentUserId(), 0)
      .then((response) => {
        if (Object.keys(response.data)[0] === "success") {
          setInConfirmedFacture(response.data.success);
        }
        setChargedFactureInfo(true);
      })
      .catch(() => showError("Erreur : il y a un problème de connexion"))
      .finally(() => stopLoading());
  };

  useEffect(() => {
    const status = localStorage.getItem("status");
    setUserRole(status === null ? ["notConnected"] : JSON.parse(status));

    Societe.getSocietesClients("CLIENT", "CLIENT", 1, 0)
      .then((reponse) => setClients(reponse.data["Societe"] || []))
      .catch(() => showError("Erreur : il y a un problème pour obtenir les clients"));

    loadLastMonth();
  }, []);

  const clientOptions = useMemo(
    () => [
      ALL_CLIENTS,
      ...clients
        .filter((c) => c?.id !== undefined)
        .map((c) => ({ value: c.id, label: c.title })),
    ],
    [clients]
  );

  // Backend expects "any" when no date, and a full timestamp otherwise.
  const dateBegin = dateBeginRaw ? `${dateBeginRaw}T00:00:00` : "any";
  const dateEnd = dateEndRaw ? `${dateEndRaw}T23:59:59` : "any";

  const onGetFacturesByFiltersHandler = () => {
    if (dateBeginRaw && dateEndRaw && dateBeginRaw > dateEndRaw) {
      showError("La date de début doit être antérieure à la date de fin");
      return;
    }
    startLoading();
    SellingService.getFactureByFiltersOptimized(
      getCurrentUserId(),
      selectedClient.value,
      0,
      dateBegin,
      dateEnd
    )
      .then((response) => {
        if (Object.keys(response.data)[0] === "success") {
          setInConfirmedFacture(response.data.success);
        } else {
          setInConfirmedFacture([]);
        }
        setChargedFactureInfo(true);
      })
      .catch(() => showError("Erreur : il y a un problème de connexion"))
      .finally(() => stopLoading());
  };

  const onResetFilters = () => {
    setSelectedClient(ALL_CLIENTS);
    setDateBeginRaw("");
    setDateEndRaw("");
    loadLastMonth();
  };

  const caHT = inConfirmedFacture.reduce((s, r) => s + (Number(r.buyingPrice) || 0), 0);
  const caTT = inConfirmedFacture.reduce((s, r) => s + (Number(r.ttPrice) || 0), 0);
  const totalTimbre = inConfirmedFacture.reduce((s, r) => s + (Number(r.timbre) || 1), 0);
  const nbAttached = inConfirmedFacture.filter(
    (r) => r.bdcReference && r.bdcReference !== "any"
  ).length;
  const nbFree = inConfirmedFacture.length - nbAttached;

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                <i className="fas fa-file-invoice mr-2"></i>Les factures de vente
              </h4>
              <small className="text-muted">
                Par défaut, les factures ajoutées durant le dernier mois
              </small>
            </div>
            <div className="d-flex flex-wrap">
              <AddButton variant="outline-dark" onClick={() => navigate("/FactureSellEtat")}>
                État des factures
              </AddButton>
              {canCreate && (
                <>
                  <AddButton variant="outline-danger" onClick={() => navigate("/addFactureAvrDepot")}>
                    Facture avoir (dépôt de prix)
                  </AddButton>
                  <AddButton variant="outline-dark" onClick={() => navigate("/addFactureSellDepot")}>
                    Facture avec dépôt de prix
                  </AddButton>
                  <AddButton onClick={() => navigate("/addFactureSell")}>
                    Nouvelle facture
                  </AddButton>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-dark mt-3" style={cardStyle}>
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-filter mr-2"></i>Filtres
              </h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label style={labelStyle}>Client</label>
                    <Select
                      closeMenuOnSelect={true}
                      components={animatedComponents}
                      value={selectedClient}
                      onChange={setSelectedClient}
                      options={clientOptions}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="factDateBegin" style={labelStyle}>
                      Date début
                    </label>
                    <input
                      id="factDateBegin"
                      type="date"
                      className="form-control"
                      value={dateBeginRaw}
                      max={dateEndRaw || undefined}
                      onChange={(e) => setDateBeginRaw(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="factDateEnd" style={labelStyle}>
                      Date fin
                    </label>
                    <input
                      id="factDateEnd"
                      type="date"
                      className="form-control"
                      value={dateEndRaw}
                      min={dateBeginRaw || undefined}
                      onChange={(e) => setDateEndRaw(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="card-footer text-right">
              <button className="btn btn-outline-dark mr-2" onClick={onResetFilters}>
                <i className="fas fa-undo mr-1"></i> Réinitialiser
              </button>
              <button className="btn btn-dark" onClick={onGetFacturesByFiltersHandler}>
                <i className="fas fa-search mr-1"></i> Valider le choix
              </button>
            </div>
          </div>

          {chargedFactureInfo && (
            <>
              <div className="row">
                <KpiCard label="CA H.T" value={fmt(caHT)} icon="fas fa-dollar-sign" color="#343a40">
                  <small className="text-muted d-block">Somme P.V.HT</small>
                </KpiCard>
                <KpiCard label="CA T.T" value={fmt(caTT)} icon="fas fa-dollar-sign" color="#007bff">
                  <small className="text-muted d-block">Somme P.V.TT</small>
                </KpiCard>
                <KpiCard
                  label="Total TVA"
                  value={fmt(caTT - caHT)}
                  icon="fas fa-hand-holding-usd"
                  color="#dc3545"
                >
                  <small className="text-muted d-block">P.V.TT - P.V.HT</small>
                </KpiCard>
                <KpiCard label="Total timbre" value={totalTimbre} icon="fas fa-stamp" color="#f0ad4e">
                  <small className="text-muted d-block">
                    {inConfirmedFacture.length} facture(s)
                  </small>
                </KpiCard>
                <KpiCard
                  label="État facture"
                  value={inConfirmedFacture.length}
                  icon="fas fa-link"
                  color="#17a2b8"
                >
                  <div>
                    <span className="badge badge-success mr-1">{nbAttached} Lié à BDC</span>
                    <span className="badge badge-secondary">{nbFree} Non lié</span>
                  </div>
                </KpiCard>
              </div>

              <FactureNonConfirme listFacture={inConfirmedFacture} />
            </>
          )}
        </div>
      </section>

      <Snackbar
        open={alert.open}
        autoHideDuration={3000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert variant="filled" onClose={handleAlertClose} severity="error">
          {alert.text}
        </Alert>
      </Snackbar>
    </div>
  );
};
export default withLoader(AllFactureSell);
